import { setSyncProgress, store } from '@/app/store';
import { setAvailableFeatures } from '@/app/store/featuresSlice';
import { fetchStarredMessages } from '@/features/chats';
import { getRecentChatDataById, setArchivedChatIds } from '@/features/chats/model';
import { clearInactiveChats } from '@/features/chats/model/conversation';
import { setKeepArchived } from '@/features/settings/model/settingsSlice';
import { SDK, getAllArchivedChats, getUserSettings } from '@/infrastructure/sdk';
import { syncQueueManager } from '@/infrastructure/sync/syncQueueManager';
import { createLogger, yieldToMain } from '@/infrastructure/sync/utils';
import { captureUnsentMessages, restoreUnsentMessages } from '@/features/chats/services/unsentRetention';
import { syncBlockedUsers } from './blockedUsers';
import { syncChats } from './chats';
import { syncGroups } from './groups';
import { InviteSync } from './invite';
import { syncMessages } from './messages';
import { syncMuteUsers } from './profiles';

const { log, logTime, flushLogs } = createLogger('SyncManager');

/**
 * Orchestrates the full synchronization process.
 * @param {Object} options - Sync options.
 * @param {boolean} options.showProgress - Whether to show the progress bar.
 */
export const performDataSync = async (options = { showProgress: true }) => {
   const { showProgress = true } = options;
   log('Sync', `🚀 Starting synchronization | showProgress=${showProgress}`);
   const startTime = performance.now();
   syncQueueManager.startSync();

   // Snapshot still-unsent messages into the buffer BEFORE any sync step clears
   // or overwrites Redux. Restored at the end so they survive the refresh.
   captureUnsentMessages();

   const dispatch = store.dispatch;
   try {
      if (showProgress) dispatch(setSyncProgress(3));
      const res = await SDK.getAvailableFeatures();
      if (res?.statusCode === 200 && res?.data) {
         dispatch(setAvailableFeatures(res.data));
      }

      await syncBlockedUsers();

      await syncMuteUsers();

      await fetchStarredMessages();

      // Step 1: Sync Groups and Features (11% -> 60%)
      await syncGroups({
         progressRange: [3, 10],
         config: { preFetch: showProgress },
      });

      // Sync Archive Settings
      const settingsResult = await getUserSettings();
      if (settingsResult?.statusCode === 200) {
         dispatch(setKeepArchived(settingsResult.data?.archive === 1));
      }

      const archivedChats = await getAllArchivedChats();
      if (archivedChats?.statusCode === 200 && Array.isArray(archivedChats.data)) {
         const archivedIds = archivedChats.data.map(chat => chat.fromUserId);
         dispatch(setArchivedChatIds(archivedIds));
      }

      log('Step 1', `Synced groups and features`);
      await yieldToMain();

      // Step 2: Sync Recent Chats (First page only, acts like refresh) (60% -> 80%)
      const { totalCount: totalChats = 0 } = await syncChats({
         progressRange: [60, 80],
         config: { preFetch: showProgress },
      });
      log('Step 2', `Synced ${totalChats} recent chats`);
      await yieldToMain();

      // Step 2.5: Sync Message Requests
      await InviteSync.getRequestUsers();
      log('Step 2.5', `Synced message requests`);
      await yieldToMain();

      // Step 3: Sync Messages for Active Conversation Only
      const state = store.getState();
      const activeChatId = state.navigationData?.activeRightStack?.activeChatId;

      // Clear inactive chats from Redux so they're pristine upon subsequent clicks
      dispatch(clearInactiveChats({ activeChatId }));

      if (activeChatId) {
         const activeChat = getRecentChatDataById(activeChatId);
         if (activeChat) {
            await syncMessages([activeChat]);
            log('Step 3', `Synced messages for active chat ${activeChatId}`);
         }
      }

      // Re-surface unsent messages now that the refresh is done: re-queue
      // text/meet for send, keep media for manual retry, and flush the active
      // chat's buffer into the synced store. Inactive chats stay buffered until
      // opened. Runs after sync so its clears/overwrites can't undo it.
      //
      // Must run BEFORE the yieldToMain below: syncMessages' setConversationMessages
      // drops the active chat's unsent messages, and yielding here would let the
      // browser paint that dropped state (a visible flicker) before the buffer
      // flush re-adds them. Keeping both in the same task → single paint, no flicker.
      restoreUnsentMessages(activeChatId);

      await yieldToMain();

      logTime('Summary', '✅ SYNC COMPLETE', startTime);
      if (showProgress) dispatch(setSyncProgress(100));
      return [];
   } catch (err) {
      log('Error', `performDataSync error: ${err.message}`);
      if (showProgress) dispatch(setSyncProgress(100)); // Ensure we don't get stuck
      return [];
   } finally {
      await syncQueueManager.endSync();
      const totalDuration = ((performance.now() - startTime) / 1000).toFixed(2);
      flushLogs(`[SyncManager] SYNC COMPLETE: UI RELEASED in ${totalDuration}s`);
   }
};
